import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, Calendar, Clock, DollarSign, FileText, AlertCircle, CheckCircle, XCircle,
  ChevronDown, Trash2, CreditCard, Coffee, MessageSquare, Send, Eye, Users, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  clarification_required: 'bg-blue-100 text-blue-800 border-blue-300'
};

const typeLabels = {
  regular_leave: 'Regular Leave',
  emergency_leave: 'Emergency Leave',
  paid_leave: 'Paid Leave',
  overtime: 'Overtime',
  reimbursement: 'Reimbursement',
  permission: 'Short Leave',
  advance: 'Advance Request'
};

const typeIcons = {
  regular_leave: Calendar,
  emergency_leave: AlertCircle,
  paid_leave: Calendar,
  overtime: Clock,
  reimbursement: DollarSign,
  permission: Coffee,
  advance: CreditCard
};

export default function MyRequestsPage() {
  const { user, getAuthHeader } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [shiftEndTime, setShiftEndTime] = useState('18:00');
  
  // Dialog states
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [validOtDates, setValidOtDates] = useState([]);
  const [loadingOtDates, setLoadingOtDates] = useState(false);
  const [selectedOtDate, setSelectedOtDate] = useState('');
  const [reimbursementDialogOpen, setReimbursementDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [clarificationDialogOpen, setClarificationDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [clarificationReply, setClarificationReply] = useState('');
  const [viewDetailOpen, setViewDetailOpen] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);
  
  // Form states
  const [leaveType, setLeaveType] = useState('regular_leave');
  const [leaveForm, setLeaveForm] = useState({ from_date: '', to_date: '', reason: '' });
  const [otForm, setOtForm] = useState({ from_time: '', to_time: '', reason: '' });
  const [reimbItems, setReimbItems] = useState([{ date: '', description: '', amount: '' }]);
  const [reimbHasInvoice, setReimbHasInvoice] = useState(false);
  const [permForm, setPermForm] = useState({ date: '', from_time: '', to_time: '', reason: '' });
  const [advForm, setAdvForm] = useState({ advance_type: 'salary', amount: '', reason: '', expected_return_date: '' });
  
  // Coverage Mode states
  const [defaultBackup, setDefaultBackup] = useState({ id: null, name: null });
  const [availableBackups, setAvailableBackups] = useState([]);
  const [selectedBackupId, setSelectedBackupId] = useState('');
  const [coveragePreview, setCoveragePreview] = useState(null);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [backupSettingsOpen, setBackupSettingsOpen] = useState(false);
  const [savingBackup, setSavingBackup] = useState(false);
  
  useEffect(() => {
    fetchRequests();
    fetchShiftEnd();
    fetchDefaultBackup();
  }, []);
  
  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/employee-requests`, { headers: getAuthHeader() });
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchShiftEnd = async () => {
    try {
      const res = await axios.get(`${API_URL}/employee-requests/my-shift-end`, { headers: getAuthHeader() });
      setShiftEndTime(res.data.shift_end || '18:00');
    } catch (error) {
      console.error('Could not fetch shift end time');
    }
  };
  
  const fetchDefaultBackup = async () => {
    try {
      const res = await axios.get(`${API_URL}/users/me`, { headers: getAuthHeader() });
      if (res.data.default_backup_id) {
        setDefaultBackup({ id: res.data.default_backup_id, name: res.data.default_backup_name || 'Unknown' });
        setSelectedBackupId(res.data.default_backup_id);
      }
    } catch (error) {
      console.error('Could not fetch default backup');
    }
  };
  
  const fetchAvailableBackups = async (fromDate, toDate) => {
    if (!fromDate || !toDate) return;
    try {
      const res = await axios.get(`${API_URL}/employee-requests/available-backups`, {
        params: { from_date: fromDate, to_date: toDate }, headers: getAuthHeader()
      });
      setAvailableBackups(res.data.available || []);
    } catch (error) {
      console.error('Could not fetch available backups');
    }
  };
  
  const fetchCoveragePreview = async (fromDate, toDate) => {
    if (!fromDate || !toDate) { setCoveragePreview(null); return; }
    setLoadingCoverage(true);
    try {
      const res = await axios.get(`${API_URL}/employee-requests/coverage-preview`, {
        params: { from_date: fromDate, to_date: toDate }, headers: getAuthHeader()
      });
      setCoveragePreview(res.data);
      if (res.data.default_backup?.id && !selectedBackupId) {
        setSelectedBackupId(res.data.default_backup.id);
      }
    } catch (error) {
      console.error('Could not fetch coverage preview');
    } finally {
      setLoadingCoverage(false);
    }
  };
  
  const handleSaveDefaultBackup = async (backupId) => {
    setSavingBackup(true);
    try {
      const res = await axios.patch(`${API_URL}/users/${user.id}/default-backup`, {
        default_backup_id: backupId || null
      }, { headers: getAuthHeader() });
      if (backupId) {
        setDefaultBackup({ id: res.data.default_backup_id, name: res.data.default_backup_name });
        toast.success(`Default backup set to ${res.data.default_backup_name}`);
      } else {
        setDefaultBackup({ id: null, name: null });
        toast.success('Default backup cleared');
      }
      setBackupSettingsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update default backup');
    } finally {
      setSavingBackup(false);
    }
  };
  
  useEffect(() => {
    if (leaveDialogOpen && leaveForm.from_date && leaveForm.to_date) {
      fetchCoveragePreview(leaveForm.from_date, leaveForm.to_date);
      fetchAvailableBackups(leaveForm.from_date, leaveForm.to_date);
    }
  }, [leaveDialogOpen, leaveForm.from_date, leaveForm.to_date]);
  
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Fetch valid OT dates when dialog opens
  const fetchValidOtDates = async () => {
    setLoadingOtDates(true);
    try {
      const response = await axios.get(`${API_URL}/employee-requests/valid-ot-dates`, { headers: getAuthHeader() });
      const dates = response.data.valid_dates || [];
      setValidOtDates(dates);
      // Auto-select the first valid (non-Sunday, non-holiday) date
      const firstValid = dates.find(d => !d.is_sunday && !d.is_holiday);
      if (firstValid) {
        setSelectedOtDate(firstValid.date);
      } else if (dates.length > 0) {
        setSelectedOtDate(dates[0].date);
      }
    } catch (error) {
      console.error('Failed to fetch valid OT dates:', error);
      // Fallback to yesterday
      setValidOtDates([{ date: getYesterday(), display: 'Yesterday', is_sunday: false, is_holiday: false }]);
      setSelectedOtDate(getYesterday());
    } finally {
      setLoadingOtDates(false);
    }
  };

  // Fetch valid OT dates when overtime dialog opens
  useEffect(() => {
    if (overtimeDialogOpen) {
      fetchValidOtDates();
    }
  }, [overtimeDialogOpen]);
  
  // Leave Request Submit
  const handleLeaveSubmit = async () => {
    if (!leaveForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/employee-requests/leave`, {
        leave_type: leaveType,
        ...leaveForm,
        coverage_backup_id: selectedBackupId || null
      }, { headers: getAuthHeader() });
      toast.success('Leave request submitted');
      setLeaveDialogOpen(false);
      setLeaveForm({ from_date: '', to_date: '', reason: '' });
      setCoveragePreview(null);
      setSelectedBackupId(defaultBackup.id || '');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };
  
  // Overtime Request Submit
  const handleOvertimeSubmit = async () => {
    if (!otForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (!selectedOtDate) {
      toast.error('Please select an OT date');
      return;
    }
    try {
      await axios.post(`${API_URL}/employee-requests/overtime`, {
        ot_date: selectedOtDate,
        ...otForm
      }, { headers: getAuthHeader() });
      toast.success('Overtime request submitted');
      setOvertimeDialogOpen(false);
      setOtForm({ from_time: '', to_time: '', reason: '' });
      setSelectedOtDate('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit overtime request');
    }
  };
  
  // Reimbursement Submit
  const handleReimbursementSubmit = async () => {
    const validItems = reimbItems.filter(item => item.date && item.description && item.amount);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }
    try {
      await axios.post(`${API_URL}/employee-requests/reimbursement`, {
        items: validItems.map(item => ({ ...item, amount: parseFloat(item.amount) })),
        has_invoice: reimbHasInvoice
      }, { headers: getAuthHeader() });
      toast.success('Reimbursement request submitted');
      setReimbursementDialogOpen(false);
      setReimbItems([{ date: '', description: '', amount: '' }]);
      setReimbHasInvoice(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit reimbursement request');
    }
  };
  
  // Short Leave Submit
  const handlePermissionSubmit = async () => {
    if (!permForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/employee-requests/permission`, permForm, { headers: getAuthHeader() });
      toast.success('Short leave request submitted');
      setPermissionDialogOpen(false);
      setPermForm({ date: '', from_time: '', to_time: '', reason: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit short leave request');
    }
  };
  
  // Advance Submit
  const handleAdvanceSubmit = async () => {
    if (!advForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (!advForm.amount || parseFloat(advForm.amount) <= 0) {
      toast.error('Valid amount is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/employee-requests/advance`, {
        ...advForm,
        amount: parseFloat(advForm.amount)
      }, { headers: getAuthHeader() });
      toast.success('Advance request submitted');
      setAdvanceDialogOpen(false);
      setAdvForm({ advance_type: 'salary', amount: '', reason: '', expected_return_date: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit advance request');
    }
  };
  
  // Cancel pending Request (original DELETE flow)
  const handleCancel = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    try {
      await axios.delete(`${API_URL}/employee-requests/${requestId}`, { headers: getAuthHeader() });
      toast.success('Request cancelled');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  // Bug 2 — Cancel an APPROVED request (full reversal, no re-edit)
  const handleCancelApproved = async (req) => {
    const leaveCovered = (req.covered_tasks || []).filter(ct => ct.status === 'covered').length;
    const msg = leaveCovered > 0
      ? `Cancel this approved ${typeLabels[req.request_type] || req.request_type}? ${leaveCovered} backup-covered SWS task(s) will revert to you.`
      : `Cancel this approved ${typeLabels[req.request_type] || req.request_type}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(
        `${API_URL}/employee-requests/${req.id}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      const reverted = res.data?.reverted_tasks_count || 0;
      toast.success(reverted > 0
        ? `Request cancelled. ${reverted} task(s) reverted to you.`
        : 'Request cancelled');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel approved request');
    }
  };
  
  // Reply to clarification
  const handleClarificationReply = async () => {
    if (!clarificationReply.trim()) {
      toast.error('Reply message is required');
      return;
    }
    try {
      await axios.patch(
        `${API_URL}/employee-requests/${selectedRequest.id}/reply-clarification`,
        { message: clarificationReply.trim() },
        { headers: getAuthHeader() }
      );
      toast.success('Reply sent successfully');
      setClarificationDialogOpen(false);
      setClarificationReply('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };
  
  // Add reimbursement item
  const addReimbItem = () => {
    setReimbItems([...reimbItems, { date: '', description: '', amount: '' }]);
  };
  
  const updateReimbItem = (index, field, value) => {
    const updated = [...reimbItems];
    updated[index][field] = value;
    setReimbItems(updated);
  };
  
  const removeReimbItem = (index) => {
    if (reimbItems.length === 1) return;
    setReimbItems(reimbItems.filter((_, i) => i !== index));
  };
  
  const reimbTotal = reimbItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  
  // Filter requests
  const filteredRequests = requests.filter(req => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return req.status === 'pending' || req.status === 'clarification_required';
    if (activeTab === 'approved') return req.status === 'approved';
    if (activeTab === 'rejected') return req.status === 'rejected';
    return true;
  });
  
  const pendingCount = requests.filter(r => r.status === 'pending' || r.status === 'clarification_required').length;
  const clarificationCount = requests.filter(r => r.status === 'clarification_required').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };
  
  // Calculate hours for OT preview
  const calculateHours = (from, to) => {
    if (!from || !to) return 0;
    const [fh, fm] = from.split(':').map(Number);
    const [th, tm] = to.split(':').map(Number);
    let fromMins = fh * 60 + fm;
    let toMins = th * 60 + tm;
    if (toMins < fromMins) toMins += 24 * 60;
    return ((toMins - fromMins) / 60).toFixed(1);
  };
  
  return (
    <DashboardLayout>
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">My Requests</h1>
          <p className="text-muted-foreground">Apply for leave, overtime, reimbursements and more</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchAvailableBackups(new Date().toISOString().split('T')[0], new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]); setBackupSettingsOpen(true); }} data-testid="backup-settings-btn">
            <Settings className="w-4 h-4 mr-2" />
            Backup Settings
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="new-request-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Request
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => { setLeaveType('regular_leave'); setLeaveDialogOpen(true); }}>
                <Calendar className="w-4 h-4 mr-2" />
                Regular Leave
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setLeaveType('emergency_leave'); setLeaveDialogOpen(true); }}>
              <AlertCircle className="w-4 h-4 mr-2" />
              Emergency Leave
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setLeaveType('paid_leave'); setLeaveDialogOpen(true); }}>
              <Calendar className="w-4 h-4 mr-2" />
              Paid Leave
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setOtForm({ ...otForm, from_time: shiftEndTime }); setOvertimeDialogOpen(true); }}>
              <Clock className="w-4 h-4 mr-2" />
              Overtime
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setReimbursementDialogOpen(true)}>
              <DollarSign className="w-4 h-4 mr-2" />
              Reimbursement
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setPermissionDialogOpen(true)}>
              <Coffee className="w-4 h-4 mr-2" />
              Short Leave / Half-Day
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAdvanceDialogOpen(true)}>
              <CreditCard className="w-4 h-4 mr-2" />
              Advance Request
            </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <Card>
        <CardHeader className="pb-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
              <TabsTrigger value="pending">
                Pending
                {pendingCount > 0 && <Badge variant="secondary" className="ml-2">{pendingCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="approved">Approved ({approvedCount})</TabsTrigger>
              <TabsTrigger value="rejected">Rejected ({rejectedCount})</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No requests found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((req) => {
                  const Icon = typeIcons[req.type] || FileText;
                  return (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">{req.request_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          {typeLabels[req.type] || req.type}
                        </div>
                      </TableCell>
                      <TableCell>
                        {req.type.includes('leave') && (
                          <span>{formatDate(req.from_date)} - {formatDate(req.to_date)} ({req.num_days} days)</span>
                        )}
                        {req.type === 'overtime' && (
                          <span>{formatDate(req.ot_date)} | {req.from_time} - {req.to_time} ({req.hours} hrs)</span>
                        )}
                        {req.type === 'reimbursement' && (
                          <span>{req.items?.length} items | {formatCurrency(req.total_amount)}</span>
                        )}
                        {req.type === 'permission' && (
                          <span>{formatDate(req.date)} | {req.from_time} - {req.to_time} ({req.hours} hrs)</span>
                        )}
                        {req.type === 'advance' && (
                          <span>{req.advance_type === 'salary' ? 'Salary' : 'Travel'} | {formatCurrency(req.amount)}</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(req.created_at)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[req.status]}>
                          {req.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {req.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {req.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                          {req.status === 'clarification_required' && <MessageSquare className="w-3 h-3 mr-1" />}
                          {req.status === 'clarification_required' ? 'Clarification' : req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                        </Badge>
                        {req.rejection_reason && (
                          <p className="text-xs text-red-600 mt-1">Reason: {req.rejection_reason}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="text-gray-600" onClick={() => { setViewRequest(req); setViewDetailOpen(true); }} title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {req.status === 'clarification_required' && (
                            <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => {
                              setSelectedRequest(req);
                              setClarificationDialogOpen(true);
                            }} data-testid={`reply-clarification-${req.id}`}>
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Reply
                            </Button>
                          )}
                          {(req.status === 'pending' || req.status === 'clarification_required') && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancel(req.id)} title="Cancel pending request" data-testid={`cancel-pending-btn-${req.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          {req.status === 'approved' && (
                            <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleCancelApproved(req)} title="Cancel approved request (reverses coverage, cannot be undone)" data-testid={`cancel-approved-btn-${req.id}`}>
                              Cancel
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Leave Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={(open) => { setLeaveDialogOpen(open); if (!open) { setCoveragePreview(null); setSelectedBackupId(defaultBackup.id || ''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply for {typeLabels[leaveType]}</DialogTitle>
            <DialogDescription>
              {leaveType === 'regular_leave' && 'Regular leave requires at least 3 days advance notice. Daily tasks will be skipped.'}
              {leaveType === 'emergency_leave' && 'For leave requests with less than 3 days notice.'}
              {leaveType === 'paid_leave' && 'Apply from your accumulated paid leave balance.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <Input type="date" value={leaveForm.from_date} onChange={(e) => setLeaveForm({...leaveForm, from_date: e.target.value})} data-testid="leave-from-date" />
              </div>
              <div>
                <Label>To Date</Label>
                <Input type="date" value={leaveForm.to_date} onChange={(e) => setLeaveForm({...leaveForm, to_date: e.target.value})} data-testid="leave-to-date" />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-2"><Users className="h-4 w-4" />Coverage Backup</Label>
              <Select value={selectedBackupId || "__none__"} onValueChange={(val) => setSelectedBackupId(val === "__none__" ? "" : val)}>
                <SelectTrigger className="mt-1" data-testid="backup-select"><SelectValue placeholder="Select backup person" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No backup</SelectItem>
                  {availableBackups.map((backup) => (<SelectItem key={backup.id} value={backup.id}>{backup.name} {defaultBackup.id === backup.id && '(Default)'}</SelectItem>))}
                </SelectContent>
              </Select>
              {!defaultBackup.id && <p className="text-xs text-amber-600 mt-1">Tip: Set a default backup in settings</p>}
            </div>
            {loadingCoverage && <div className="text-sm text-muted-foreground text-center py-2">Loading task preview...</div>}
            {coveragePreview && !loadingCoverage && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-2">
                <div className="font-medium flex items-center gap-2"><FileText className="h-4 w-4" />Task Coverage Preview</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {leaveType === 'regular_leave' && coveragePreview.summary.daily_checklists_to_skip > 0 && <div className="bg-amber-100 rounded px-2 py-1"><span className="font-medium">{coveragePreview.summary.daily_checklists_to_skip}</span> daily tasks will be skipped</div>}
                  {coveragePreview.summary.sws_tasks_to_cover > 0 && <div className="bg-blue-100 rounded px-2 py-1"><span className="font-medium">{coveragePreview.summary.sws_tasks_to_cover}</span> tasks to backup</div>}
                  {coveragePreview.summary.sws_tasks_on_hold > 0 && <div className="bg-gray-100 rounded px-2 py-1"><span className="font-medium">{coveragePreview.summary.sws_tasks_on_hold}</span> tasks on hold</div>}
                  {coveragePreview.summary.total_affected === 0 && <div className="col-span-2 text-muted-foreground">No tasks affected during this period</div>}
                </div>
              </div>
            )}
            <div>
              <Label>Reason *</Label>
              <Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} placeholder="Enter reason for leave..." data-testid="leave-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLeaveSubmit} data-testid="submit-leave-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Overtime Dialog */}
      <Dialog open={overtimeDialogOpen} onOpenChange={setOvertimeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for Overtime</DialogTitle>
            <DialogDescription>
              Select a valid OT date. Sundays and holidays are skipped. Rate: 1x
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>OT Date *</Label>
              {loadingOtDates ? (
                <div className="h-10 bg-muted rounded-md flex items-center justify-center text-sm text-muted-foreground">
                  Loading valid dates...
                </div>
              ) : (
                <Select value={selectedOtDate} onValueChange={setSelectedOtDate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select OT date" />
                  </SelectTrigger>
                  <SelectContent>
                    {validOtDates.map((d) => (
                      <SelectItem 
                        key={d.date} 
                        value={d.date}
                        className={d.is_sunday || d.is_holiday ? 'text-muted-foreground' : ''}
                      >
                        {d.display}
                        {d.is_sunday && <span className="ml-2 text-xs text-amber-600">(Sunday)</span>}
                        {d.is_holiday && <span className="ml-2 text-xs text-red-600">(Holiday)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {validOtDates.length > 1 
                  ? 'Multiple dates available due to Sunday/Holiday' 
                  : 'OT applies to the last working day'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Time</Label>
                <Input type="time" value={otForm.from_time} onChange={(e) => setOtForm({...otForm, from_time: e.target.value})} />
                <p className="text-xs text-muted-foreground mt-1">Shift end: {shiftEndTime}</p>
              </div>
              <div>
                <Label>To Time</Label>
                <Input type="time" value={otForm.to_time} onChange={(e) => setOtForm({...otForm, to_time: e.target.value})} />
              </div>
            </div>
            {otForm.from_time && otForm.to_time && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium">Total OT Hours: {calculateHours(otForm.from_time, otForm.to_time)} hours</p>
              </div>
            )}
            <div>
              <Label>Reason for OT *</Label>
              <Textarea value={otForm.reason} onChange={(e) => setOtForm({...otForm, reason: e.target.value})} placeholder="Why was overtime required?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOvertimeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleOvertimeSubmit} data-testid="submit-ot-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reimbursement Dialog */}
      <Dialog open={reimbursementDialogOpen} onOpenChange={setReimbursementDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reimbursement Request</DialogTitle>
            <DialogDescription>
              Add all expense items with dates and amounts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-32">Amount (₹)</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reimbItems.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Input type="date" value={item.date} onChange={(e) => updateReimbItem(idx, 'date', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <Input value={item.description} onChange={(e) => updateReimbItem(idx, 'description', e.target.value)} placeholder="Expense description" />
                    </TableCell>
                    <TableCell>
                      <Input type="number" value={item.amount} onChange={(e) => updateReimbItem(idx, 'amount', e.target.value)} placeholder="0" />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => removeReimbItem(idx)} disabled={reimbItems.length === 1}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-between items-center">
              <Button variant="outline" size="sm" onClick={addReimbItem}>
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-xl font-bold">{formatCurrency(reimbTotal)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="has-invoice"
                checked={reimbHasInvoice} 
                onCheckedChange={(checked) => setReimbHasInvoice(checked)} 
                data-testid="reimb-has-invoice-checkbox"
              />
              <Label htmlFor="has-invoice" className="text-sm cursor-pointer">I have invoices/bills for these expenses</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReimbursementDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReimbursementSubmit} data-testid="submit-reimb-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Permission Dialog */}
      <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Short Leave / Half-Day Request</DialogTitle>
            <DialogDescription>
              For short absences up to 4 hours
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input type="date" value={permForm.date} onChange={(e) => setPermForm({...permForm, date: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Time</Label>
                <Input type="time" value={permForm.from_time} onChange={(e) => setPermForm({...permForm, from_time: e.target.value})} />
              </div>
              <div>
                <Label>To Time</Label>
                <Input type="time" value={permForm.to_time} onChange={(e) => setPermForm({...permForm, to_time: e.target.value})} />
              </div>
            </div>
            {permForm.from_time && permForm.to_time && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium">Duration: {calculateHours(permForm.from_time, permForm.to_time)} hours</p>
                {parseFloat(calculateHours(permForm.from_time, permForm.to_time)) > 4 && (
                  <p className="text-xs text-red-600 mt-1">Maximum allowed is 4 hours. Please apply for leave instead.</p>
                )}
              </div>
            )}
            <div>
              <Label>Reason *</Label>
              <Textarea value={permForm.reason} onChange={(e) => setPermForm({...permForm, reason: e.target.value})} placeholder="Reason for short leave..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePermissionSubmit} data-testid="submit-perm-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Advance Request Dialog */}
      <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Advance Request</DialogTitle>
            <DialogDescription>
              Request salary advance or travel advance
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Advance Type</Label>
              <Select value={advForm.advance_type} onValueChange={(v) => setAdvForm({...advForm, advance_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary">Salary Advance</SelectItem>
                  <SelectItem value="travel">Travel Advance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" value={advForm.amount} onChange={(e) => setAdvForm({...advForm, amount: e.target.value})} placeholder="Enter amount" />
            </div>
            {advForm.advance_type === 'travel' && (
              <div>
                <Label>Expected Return Date</Label>
                <Input type="date" value={advForm.expected_return_date} onChange={(e) => setAdvForm({...advForm, expected_return_date: e.target.value})} />
              </div>
            )}
            <div>
              <Label>Reason *</Label>
              <Textarea value={advForm.reason} onChange={(e) => setAdvForm({...advForm, reason: e.target.value})} placeholder="Reason for advance..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdvanceSubmit} data-testid="submit-adv-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Request Detail Dialog */}
      <Dialog open={viewDetailOpen} onOpenChange={(open) => { if (!open) { setViewDetailOpen(false); setViewRequest(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Details — {viewRequest?.request_number}</DialogTitle>
            <DialogDescription>
              Submitted on {viewRequest ? new Date(viewRequest.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
            </DialogDescription>
          </DialogHeader>
          {viewRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{typeLabels[viewRequest.type] || viewRequest.type}</span></div>
                <div><span className="text-muted-foreground">Status:</span> <Badge className={statusColors[viewRequest.status] + ' ml-1'}>{viewRequest.status === 'clarification_required' ? 'Clarification' : viewRequest.status.charAt(0).toUpperCase() + viewRequest.status.slice(1)}</Badge></div>
                {viewRequest.type?.includes('leave') && (<>
                  <div><span className="text-muted-foreground">From:</span> {formatDate(viewRequest.from_date)}</div>
                  <div><span className="text-muted-foreground">To:</span> {formatDate(viewRequest.to_date)} ({viewRequest.num_days} days)</div>
                </>)}
                {viewRequest.type === 'overtime' && (<>
                  <div><span className="text-muted-foreground">OT Date:</span> {formatDate(viewRequest.ot_date)}</div>
                  <div><span className="text-muted-foreground">Time:</span> {viewRequest.from_time} - {viewRequest.to_time} ({viewRequest.hours} hrs)</div>
                </>)}
                {viewRequest.type === 'permission' && (<>
                  <div><span className="text-muted-foreground">Date:</span> {formatDate(viewRequest.date)}</div>
                  <div><span className="text-muted-foreground">Time:</span> {viewRequest.from_time} - {viewRequest.to_time} ({viewRequest.hours} hrs)</div>
                </>)}
                {viewRequest.type === 'advance' && (<>
                  <div><span className="text-muted-foreground">Advance Type:</span> {viewRequest.advance_type === 'salary' ? 'Salary' : 'Travel'}</div>
                  <div><span className="text-muted-foreground">Amount:</span> {formatCurrency(viewRequest.amount)}</div>
                  {viewRequest.expected_return_date && <div className="col-span-2"><span className="text-muted-foreground">Expected Return:</span> {formatDate(viewRequest.expected_return_date)}</div>}
                </>)}
                {viewRequest.type === 'reimbursement' && (<>
                  <div><span className="text-muted-foreground">Total:</span> {formatCurrency(viewRequest.total_amount)}</div>
                  <div><span className="text-muted-foreground">Invoice Available:</span> {viewRequest.has_invoice ? 'Yes' : 'No'}</div>
                </>)}
              </div>
              {viewRequest.reason && (
                <div className="text-sm"><span className="text-muted-foreground">Reason:</span> <span>{viewRequest.reason}</span></div>
              )}
              {viewRequest.type === 'reimbursement' && viewRequest.items?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader><TableRow className="bg-muted/30"><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {viewRequest.items.map((item, idx) => (
                        <TableRow key={idx}><TableCell className="text-sm">{formatDate(item.date)}</TableCell><TableCell className="text-sm">{item.description}</TableCell><TableCell className="text-sm text-right">{formatCurrency(item.amount)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {viewRequest.rejection_reason && (
                <div className="text-sm p-2 bg-red-50 rounded border-l-2 border-red-400"><span className="text-muted-foreground">Rejection Reason:</span> {viewRequest.rejection_reason}</div>
              )}
              {viewRequest.approved_by_name && (
                <div className="text-sm text-muted-foreground">{viewRequest.status === 'approved' ? 'Approved' : 'Processed'} by {viewRequest.approved_by_name} on {viewRequest.approved_at ? new Date(viewRequest.approved_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
              )}
              {viewRequest.clarification_thread?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Clarification Thread</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                    {viewRequest.clarification_thread.map((entry, idx) => (
                      <div key={idx} className={`p-2 rounded-lg text-sm ${entry.role === 'approver' ? 'bg-blue-50 border-l-2 border-blue-400' : 'bg-green-50 border-l-2 border-green-400'}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-xs">{entry.from_name} ({entry.role === 'approver' ? 'Owner' : 'You'})</span>
                          <span className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p>{entry.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewDetailOpen(false); setViewRequest(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Clarification Reply Dialog */}
      <Dialog open={clarificationDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setClarificationDialogOpen(false);
          setClarificationReply('');
          setSelectedRequest(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Clarification Required - {selectedRequest?.request_number}</DialogTitle>
            <DialogDescription>
              The approver has requested more information. Please reply below.
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              {/* Thread */}
              {selectedRequest.clarification_thread?.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                  {selectedRequest.clarification_thread.map((entry, idx) => (
                    <div key={idx} className={`p-2 rounded-lg text-sm ${entry.role === 'approver' ? 'bg-blue-50 border-l-2 border-blue-400' : 'bg-green-50 border-l-2 border-green-400'}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-xs">{entry.from_name} ({entry.role === 'approver' ? 'Owner' : 'You'})</span>
                        <span className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p>{entry.message}</p>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label>Your Reply *</Label>
                <Textarea 
                  value={clarificationReply} 
                  onChange={(e) => setClarificationReply(e.target.value)} 
                  placeholder="Provide the requested information..."
                  data-testid="clarification-reply-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClarificationDialogOpen(false); setClarificationReply(''); setSelectedRequest(null); }}>Cancel</Button>
            <Button onClick={handleClarificationReply} data-testid="send-clarification-reply-btn">
              <Send className="w-4 h-4 mr-2" />
              Send Reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Backup Settings Dialog */}
      <Dialog open={backupSettingsOpen} onOpenChange={setBackupSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Default Backup Settings</DialogTitle>
            <DialogDescription>Set a default backup person who will cover your tasks when you are on leave.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Default Backup</Label>
              <div className="mt-1 p-3 bg-slate-50 rounded-lg">
                {defaultBackup.id ? (
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{defaultBackup.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleSaveDefaultBackup(null)} disabled={savingBackup}>Clear</Button>
                  </div>
                ) : (<span className="text-muted-foreground">No default backup set</span>)}
              </div>
            </div>
            <div>
              <Label>Select New Default Backup</Label>
              <Select onValueChange={(value) => handleSaveDefaultBackup(value)} disabled={savingBackup}>
                <SelectTrigger className="mt-1" data-testid="default-backup-select"><SelectValue placeholder="Choose a colleague" /></SelectTrigger>
                <SelectContent>
                  {availableBackups.map((backup) => (<SelectItem key={backup.id} value={backup.id}>{backup.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBackupSettingsOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
